import * as React from 'react';
import { useToggle } from 'unbrace/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { makeGetNotifications } from '../redux/selectors';
import { notificationActions } from '../redux/creators';
import { Drawer, Heading, Text } from 'unbrace/components';
import { Notification } from './Notification';
import { NotificationsBox } from './NotificationsBox';
import { NotificationType, NotificationStatus } from '../types';
import { Empty } from './Empty';
import { MarkAllAsReadAnchor } from './MarkAllAsReadAnchor';

type RenderProps = {
  toggleDrawer: () => void;
  count: number;
};

type Props = {
  children?: (props: RenderProps) => JSX.Element;
  title?: string;
  closeTooltip?: string;
  position?: 'left' | 'right';
  emptyResources: {
    title: string;
    description: string;
  };
  markAsReadResources: {
    single: string;
    all: string;
  };
};

const Notifications: React.FC<Props> = ({
  children,
  title,
  closeTooltip,
  position,
  emptyResources,
  markAsReadResources,
}) => {
  const [isOpen, handleToggle] = useToggle(false);
  const notifications = useSelector(makeGetNotifications());
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(notificationActions.getNotifications());
  }, [dispatch]);

  const handleMarkAllAsReadClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      dispatch(notificationActions.markAllNotificationsAsRead());
    },
    [dispatch]
  );

  const handleMarkSingleAsReadClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, notificationId: string) => {
      event.preventDefault();
      dispatch(notificationActions.markNotificationAsRead(notificationId));
    },
    [dispatch]
  );

  const unReadCount = React.useMemo(
    () =>
      notifications ? notifications.filter((notification) => notification.status === NotificationStatus.New).length : 0,
    [notifications]
  );

  return (
    <React.Fragment>
      {children && children({ toggleDrawer: handleToggle, count: unReadCount })}
      <Drawer
        open={isOpen}
        closeable
        onCloseDrawer={handleToggle}
        title={title}
        closeTooltip={closeTooltip}
        position={position}
      >
        <MarkAllAsReadAnchor href="/" onClick={handleMarkAllAsReadClick}>
          <Text asTag="span" size="xxs" color="primary" colorLevel="medium">
            {markAsReadResources.all}
          </Text>
        </MarkAllAsReadAnchor>
        {notifications && notifications.length > 0 ? (
          <NotificationsBox>
            {notifications.map((notification: NotificationType) => (
              <Notification
                key={notification.notificationId}
                message={notification.message}
                timeStamp={notification.timeStamp}
                status={notification.status}
                markAsReadResource={markAsReadResources.single}
                onMarkAsRead={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
                  handleMarkSingleAsReadClick(event, notification.notificationId)
                }
              />
            ))}
          </NotificationsBox>
        ) : (
          <Empty>
            <Heading asTag="h4" size="xs">
              {emptyResources.title}
            </Heading>
            <Text asTag="p" size="s" color="neutral" colorLevel="medium">
              {emptyResources.description}
            </Text>
          </Empty>
        )}
      </Drawer>
    </React.Fragment>
  );
};

export default Notifications;
